import { ManualSubmissionFileType } from "services/KnowfullyAdminClient";

export enum MediaType {
  IMAGE = "image",
  DOCUMENT = "document",
  AUDIO = "audio",
  VIDEO = "video",
  ARCHIVE = "archive",
}

export const allMediaLibraryTypes = [
  MediaType.IMAGE,
  MediaType.VIDEO,
  MediaType.AUDIO,
  MediaType.DOCUMENT,
];

export enum SpecificFormat {
  MP4 = "mp4",
  ZIP = "zip",
  PDF = "pdf",
}

export type MediaFilterType = MediaType | SpecificFormat;

const archiveContentTypes = [
  "application/zip",
  "application/x-zip-compressed",
  "application/x-rar-compressed",
  "application/x-7z-compressed",
  "application/x-tar",
  "application/x-gzip",
];

const specificFormatMap: Record<string, SpecificFormat> = {
  "application/zip": SpecificFormat.ZIP,
  "application/x-zip-compressed": SpecificFormat.ZIP,
  "video/mp4": SpecificFormat.MP4,
  "application/pdf": SpecificFormat.PDF,
};

const mediaTypeMap: Record<string, MediaType> = {
  image: MediaType.IMAGE,
  audio: MediaType.AUDIO,
  video: MediaType.VIDEO,
  application: MediaType.DOCUMENT,
  text: MediaType.DOCUMENT,
};

export function getMediaType(contentType: string): MediaType | null {
  const lowerContentType = contentType.toLowerCase();
  
  if (archiveContentTypes.includes(lowerContentType)) {
    return MediaType.ARCHIVE;
  }
  
  const genericType = lowerContentType.split("/")[0];
  return mediaTypeMap[genericType] ?? null;
}

export function getSpecificFormat(contentType: string): SpecificFormat | null {
  return specificFormatMap[contentType] ?? null;
}

export function matchesFilter(contentType: string, filterValues: MediaFilterType[]): boolean {
  if (filterValues.length === 0) return true;

  const mediaType = getMediaType(contentType);
  const specificFormat = specificFormatMap[contentType];

  return filterValues.some(filter => mediaType === filter || specificFormat === filter);
}

export const acceptedMedia = [
  // Images
  "image/png", // PNG image files
  "image/jpeg", // JPEG image files
  "image/gif", // GIF image files
  "image/x-icon", // Icon files (.ico)
  "image/heic", // High Efficiency Image Format files (.heic)
  "image/svg+xml", // SVG (Scalable Vector Graphics) files
  "image/webp", // WebP image files, a modern image format that provides superior lossless and lossy compression

  // Documents
  "application/pdf", // PDF (Portable Document Format) files
  "application/msword", // Older Word document files (.doc)
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // Newer Word document files (.docx)
  "text/plain", // Plain text files (.txt)
  "text/css", // CSS (Cascading Style Sheets) files
  "text/csv",
  "application/vnd.apple.keynote", // Apple Keynote presentation files (.key)
  "application/vnd.oasis.opendocument.text", // OpenDocument text files, an open standard for word processing documents (.odt)
  "application/vnd.ms-powerpoint", // Older PowerPoint presentation files (.ppt)
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // Newer PowerPoint presentation files (.pptx)
  "application/vnd.ms-powerpoint.presentation.macroEnabled.12", // Macro-enabled PowerPoint presentation files (.pptm)
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow", // PowerPoint slideshow files (.ppsx)
  "application/vnd.ms-excel", // Older Excel spreadsheet files (.xls)
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // Newer Excel spreadsheet files (.xlsx)

  // Audio
  "audio/mpeg", // MP3 audio files
  "audio/x-m4a", // M4A audio files (commonly used by Apple devices)
  "audio/ogg", // OGG audio files (open container format)
  "audio/wav", // WAV audio files (lossless format)

  // Video
  "video/x-msvideo", // AVI video files
  "video/mpeg", // MPEG video files
  "video/mp4", // MP4 video files
  "video/x-m4v", // M4V video files (commonly used by Apple devices)
  "video/quicktime", // MOV video files (commonly used by Apple devices)
  "video/ogg", // OGG video files
  "application/x-subrip", // SubRip subtitle files (.srt)
  "video/x-ms-wmv", // WMV (Windows Media Video) files
  "video/3gpp", // 3GPP video files, a multimedia container format used for 3G mobile phones
  "video/3gpp2", // 3GPP2 video files, a multimedia container format used for some 3G mobile phones

  // Archives
  ...archiveContentTypes,
];

export const FileTypes = {
  Xlsx: ".xlsx",
  Csv: ".csv",
};

export const FileSizes = {
  Mb10: 10 * 1024 * 1024, 
  Kb250: 250 * 1024,
};

export function getContentTypesForFilters(filters: MediaFilterType[]) {
  return acceptedMedia.filter(contentType => matchesFilter(contentType, filters));
}

export function getFileExtension(fileType?: ManualSubmissionFileType): string {
  const defaultType = ManualSubmissionFileType.Csv;
  return FileTypes[fileType ?? defaultType];
}
